import type { TextFieldProps } from '@mui/material'
import { TextField } from '@mui/material'
import type { ForwardedRef } from 'react'
import { forwardRef, useEffect, useState } from 'react'

export type FloatTextFieldProps = {
  textFieldProps?: TextFieldProps
  initialValue?: string
  onValueChange: (value: number | undefined) => void
  min?: number
  max?: number
  numberOfDecimals?: number
  error?: boolean
  /** When set, renders a native number input with the browser's up/down steppers using this step. */
  step?: number
}

/**
 * Renders a float text field component.
 *
 * You can specify the minimum (inclusive), maximum (inclusive), and max number of decimals (defaults to 1 if not specified).
 * Whenever the value changes, and is a valid number meeting the requirements, `onValueChange` will be called.
 *
 * Anything inside `textFieldProps` will be passed to the MUI TextField
 *
 * @param {FloatTextFieldProps} props - The props for the float text field.
 * @return {ReactNode} The float text field component.
 */
const FloatTextField = (
  { initialValue, error, ...props }: FloatTextFieldProps,
  ref: ForwardedRef<HTMLInputElement>,
) => {
  // `initialValue` is declared `string`, but the Controller wrappers seed it straight from the form
  // value, which is a number for numeric fields. This is the only place it enters the component, so
  // coercing here keeps `stringValue` genuinely a string for every reader below (SUPP-4765).
  const [stringValue, setStringValue] = useState<string | undefined>(initialValue?.toString())
  const [isInvalidValue, setIsInvalidValue] = useState<boolean>(false)
  const [floatValue, setFloatValue] = useState<number | undefined>()
  const validationRegex = new RegExp('^\\-?\\d*\\.?\\d{0,' + (props.numberOfDecimals ?? 2) + '}$')

  useEffect(() => {
    props.onValueChange(floatValue)
  }, [floatValue])

  useEffect(() => {
    validateAndUpdateFloatValue(stringValue)
  }, [stringValue])

  const handleOnChange = (event: { target: { value: any } }) => {
    const value = event.target.value
    const isValidNumber = validationRegex.test(value)

    if (isValidNumber) {
      setStringValue(value)
    }
  }

  const handleOnBlur = () => {
    const parsedFloat = validateAndUpdateFloatValue(stringValue)
    setStringValue(parsedFloat?.toString())
  }

  const meetsMinimum = (value: number) => {
    if (props.min == undefined) {
      return true
    }
    return value >= props.min
  }

  const meetsMaximum = (value: number) => {
    if (props.max == undefined) {
      return true
    }
    return value <= props.max
  }

  const validateAndUpdateFloatValue = (value?: string): number | undefined => {
    // An empty / whitespace-only input is "no value", not an invalid one — clearing an optional
    // field must not paint it red. parseFloat('') is NaN, so without this guard every empty field
    // (e.g. an unfilled optional number whose caller seeds `initialValue=''`) renders as invalid.
    if (value == undefined || value.trim() === '') {
      setIsInvalidValue(false)
      setFloatValue(undefined)
      return undefined
    }

    const parsedToFloat = Number.parseFloat(value)
    if (Number.isNaN(parsedToFloat)) {
      setIsInvalidValue(true)
      setFloatValue(undefined)
      return undefined
    }

    if (!meetsMinimum(parsedToFloat)) {
      setIsInvalidValue(true)
      setFloatValue(props.min)
      return props.min
    }

    if (!meetsMaximum(parsedToFloat)) {
      setIsInvalidValue(true)
      setFloatValue(props.max)
      return props.max
    }

    setIsInvalidValue(false)
    setFloatValue(parsedToFloat)
    return parsedToFloat
  }

  const hasStep = props.step !== undefined
  // Caller's textFieldProps.inputProps should NOT silently override our prop-driven `step` (or the
  // matching `type="number"` we set below) — those are the explicit FloatTextField API surface.
  const inputProps = {
    ref,
    ...props.textFieldProps?.inputProps,
    ...(hasStep ? { step: props.step } : {}),
  }

  return (
    <TextField
      {...props.textFieldProps}
      error={error || isInvalidValue}
      inputProps={inputProps}
      type={hasStep ? 'number' : props.textFieldProps?.type}
      value={stringValue ?? ''}
      onBlur={handleOnBlur}
      onChange={handleOnChange}
    />
  )
}

export default forwardRef(FloatTextField)
